/*
Author: Jude Fernando
Date: 25/09/2024
Copyright © 2024 Infix!. All rights reserved.
*/

import mandatoryAnswer from '@/assets/mandatoryAnswer.svg'
import Accordion from '@/components/Accordian/Accordian'
import { useUrlType } from '@/components/hooks'
import { alerts } from '@/constants'
import { PoaPdfFormHeadingClassName, TeamReviewSection } from '@/constants/constants'
import { Actions } from '@/constants/constants'
import { RootState } from '@/reducers/store'
import { Utility, Validate } from '@/utils'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useUpdateSectionAlert, useUpdateSectionValidation } from '@/state/survey/poaSurveySlice'
import { FormValues, IAnswers } from '../SurveyModel/types/commonTypes'
import { INode } from '../SurveyModel/types/nodeTypes'

const Section = (props: any) => {
    const { children, node } = props
    const { isPatient } = useUrlType()
    const form = useSelector((state: RootState) => state.form)
    const { questionnaireDetails, sections } = useSelector(
        (state: RootState) => state.poaSurvey
    )
    const [alertsErrorStatus, setAlertsErrorStatus] = useState<boolean>(false)
    const [validationErrorStatus, setValidationErrorStatus] =
        useState<boolean>(false)
    const [nodesWithExpression, setNodesWithExpression] = useState<INode[]>([])
    const [actionErrorStatus, setActionErrorStatus] = useState<boolean>(false)
    const [nodesWithValidations, setNodesWithValidations] = useState<INode[]>(
        []
    )
    const [hideSection, setHideSection] = useState<boolean>(true)
    const { selectedTab } = useSelector((state: RootState) => state.poaSurvey)
    const [nodesWithActions, setNodesWithActions] = useState<INode[]>([])
    const setSectionValidationStatus = useUpdateSectionValidation()
    const updateSectionAlert = useUpdateSectionAlert();

    const flatNodeList = useMemo(() => {
        return Utility.getFlatNodeList(node, form);
    }, [questionnaireDetails, Object.keys(form).length]);

    // Extract nodes with expressions on mount
    useEffect(() => {
        const extractedNodes: INode[] = []
        const extractedValidationNodes: INode[] = []
        if (questionnaireDetails && questionnaireDetails.nodes) {
            questionnaireDetails.nodes.forEach(
                (section) =>
                    node.name === section.name &&
                    extractNodesWithExpression(
                        section,
                        extractedNodes,
                        extractedValidationNodes
                    )
            )
            setNodesWithExpression(extractedNodes)
            setNodesWithValidations(extractedValidationNodes)
        }
    }, [questionnaireDetails, form, flatNodeList])


    const extractNodesWithExpression = (
        node: INode,
        expressionsResult: INode[],
        validationsResult: INode[],
        isRepeatedNode: boolean = false,
    ): void => {
        // Check showIf expression before processing
        if (node.showIf && !isRepeatedNode && !Utility.evaluateExp(node, form)) {
            return; // Skip this node if showIf evaluates to false
        }

        const hasExpressionAlert = node.alerts?.some(
            (alert) => alert.expression !== undefined
        )
        const hasRequiredValidation = node.validations?.some(
            (validation) => validation.type === 'required' || validation.type === 'requiredIf'
        )

        // For repeated nodes, we need to check all form keys that match the pattern
        if (isRepeatedNode) {
            // Find all form keys that start with the node name followed by a dash
            // filter by element in the dom since some repeated nodes might not be rendered if they are nested inside another repeated node with a showIf condition that evaluates to false
            const repeatedNodeKeys = Object.keys(form).filter(key => 
                key.startsWith(node.name + '-') && document.getElementById(key)
            )
            // Add a node entry for each repeated instance
            repeatedNodeKeys.forEach(key => {
                const nodeWithActualName = { ...node, name: key }
                const hash = Utility.extractNodeHash(key, true);
                if (node.showIf) {
                    let processedExpr = Utility.processFieldPlaceholders(node.showIf, hash, form);
                    let exprResult = Utility.evaluateManually(form, processedExpr);
                    if (!exprResult) {
                        // check to see if the expr contains non repeated field reference, if so process the expr without hash
                        processedExpr = Utility.processFieldPlaceholders(node.showIf, undefined, form);
                        exprResult = Utility.evaluateManually(form, processedExpr);
                    }
                    if (!exprResult) {
                        return; // Skip this instance if showIf evaluates to false
                    }
                }
                if (hasExpressionAlert) {
                    expressionsResult.push(nodeWithActualName)
                }
                if (hasRequiredValidation) {
                    validationsResult.push(nodeWithActualName)
                }
            })
        } else {
            // Regular node handling
            if (hasExpressionAlert) {
                expressionsResult.push(node)
            }
            if (hasRequiredValidation) {
                validationsResult.push(node)
            }
        }

        if (node.type === Actions) {
            setNodesWithActions([...nodesWithActions, node])
        }
        node.repeatedNodes?.forEach((childNode) =>
            extractNodesWithExpression(
                childNode,
                expressionsResult,
                validationsResult,
                true
            )
        )
        node.nodes?.forEach((childNode) =>
            extractNodesWithExpression(
                childNode,
                expressionsResult,
                validationsResult,
                isRepeatedNode || false
            )
        )
    }


    const watchedKeys = useMemo(
        () => nodesWithExpression.map((node) => node.name),
        [nodesWithExpression]
    )

    const watchedValues = useMemo(
        () => watchedKeys.map((key) => form[key]?.value),
        [form, watchedKeys]
    )

    const watchActionKeys = useMemo(
        () => nodesWithActions.map((node) => node.name),
        [nodesWithActions]
    )

    const watchedActionValues = useMemo(
        () => watchActionKeys.map((key) => form[key]?.value),
        [form, watchActionKeys]
    )

    const watchedValidationKeys = useMemo(
        () => nodesWithValidations.map((node) => node.name),
        [nodesWithValidations]
    )

    const watchedValidationValues = useMemo(
        () => watchedValidationKeys.map((key) => form[key]),
        [form, watchedValidationKeys]
    )

    const isActive = sections?.[node.name]?.isActive ?? false

    useEffect(() => {
        let hasAlert = false
        nodesWithExpression.forEach((node: INode) => {
            if (node.alerts) {
                let error: number | boolean = false
                node.alerts.some((alert) => {
                    error = Utility.evaluateManually(
                        form,
                        alert.expression,
                    )
                    if (error) {
                        hasAlert = true
                        setAlertsErrorStatus(true)
                        return true // Stops the loop
                    }
                    return false
                })
            }
        })
        if (!hasAlert) {
            setAlertsErrorStatus(false)
        }
        updateSectionAlert(node.name, hasAlert);
    }, [nodesWithExpression, ...watchedValues])

    useEffect(() => {
        setActionErrorStatus(false)
        if (nodesWithActions && watchedActionValues) {
            nodesWithActions.forEach((actionNode: INode) => {
                setActionAlert(actionNode, watchedActionValues)
            })
        }
    }, [nodesWithActions, ...watchedActionValues])

    const setActionAlert = (
        nodeWithAction: INode,
        watchedActionValues: FormValues[]
    ) => {
        const findExcludeOptionNode = (Anode: INode) => {
            if (Anode.alerts) {
                Anode.alerts?.some((alert) => {
                    if (alert.type === alerts.action) {
                        console.log(Anode, 'Anode')
                        console.log(watchedActionValues)
                        const input = alert.expression
                        const match = input.match(/{(.*?)}/)
                        const extractedValue = match ? match[1] : ''
                        watchedActionValues.forEach((value: FormValues) => {
                            if (value) {
                                const formObj = JSON.parse(value as string)
                                formObj.forEach((element: IAnswers[]) => {
                                    const answerKey: IAnswers | undefined =
                                        element.find(
                                            (answer) =>
                                                answer.name === Anode.name
                                        )
                                    const condition: IAnswers | undefined =
                                        element.find(
                                            (answer) =>
                                                answer.name === extractedValue
                                        )
                                    if (
                                        answerKey &&
                                        answerKey.value !== '' &&
                                        condition &&
                                        (condition.value == '' ||
                                            condition.value === 'false')
                                    ) {
                                        setActionErrorStatus(true)
                                    }
                                })
                            }
                        })
                    }
                })
            }

            if (Anode.nodes) {
                Anode.nodes.forEach((subNode: INode) => {
                    findExcludeOptionNode(subNode)
                })
            }
            if (Anode.repeatedNodes) {
                Anode.repeatedNodes.forEach((subNode: INode) => {
                    findExcludeOptionNode(subNode)
                })
            }
        }
        findExcludeOptionNode(nodeWithAction)
    }

    useEffect(() => {
        const hideValue =
            node.configurations &&
            node.configurations.find(
                (config: any) => config.key === 'HideInPatientAnswers'
            ) &&
            Utility.getValueFromKey(node, 'HideInPatientAnswers') === 'true'
                ? true
                : false
        setHideSection(hideValue as boolean)
    }, [node])

    useEffect(() => {
        let hasValidation = false
        nodesWithValidations.forEach((node: INode) => {
            // Skip nodes whose form entry has been removed (e.g. a deleted repeatable row)
            if (!form[node.name]) return

            const errors = Validate.errors(form[node.name])
            if (errors.length) {
                hasValidation = true
                setValidationErrorStatus(true)
            }
        })
        if (!hasValidation) {
            setValidationErrorStatus(false)
        }
    }, [nodesWithValidations, ...watchedValidationValues])

    useEffect(() => {
        setSectionValidationStatus(node.name, !validationErrorStatus)
    }, [validationErrorStatus]);

    if (isPatient) {
        return <div className="section-wrapper">{children}</div>
    }
    console.log(hideSection, 'hideSection hideSection hideSection')
    console.log(selectedTab, 'selectedTab selectedTab hideSection')
    if (!isPatient && hideSection && selectedTab === 'patient') return null

    return (
        <div
            className={`${!isActive ? 'accordion-inactive' : ''} section-wrapper`}
        >
            <div
                className={`section-header ${
                    node.name === TeamReviewSection ? PoaPdfFormHeadingClassName : ''
                } ${actionErrorStatus ? 'blue-strip-main' : ''} ${
                    alertsErrorStatus ? 'red-strip-main' : ''
                }`}
            >
                <div className="grid grid-cols-2 gap-4 items-center">
                    <h2 className="section-title">
                        {node.title}
                        <span className="section-error-icon">
                            {validationErrorStatus && (
                                <img
                                    src={mandatoryAnswer}
                                    alt="error"
                                    className="error-icon-img"
                                />
                            )}
                        </span>
                    </h2>
                    <div className="justify-self-end">
                        <Accordion level="child" section={node.name} />
                    </div>
                </div>
            </div>
    
                <div className={`grid grid-cols-6 gap-0 ${!isActive ? 'hidden' : ''}`}>
                    <div className="col-span-1 md:hidden lg:block"></div>
                    <div className="md:col-span-6 lg:col-span-4">
                        {children}
                    </div>
                    <div className="col-span-1 md:hidden lg:block"></div>
                </div>
        </div>
    )
}

export default Section
